import React from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { Flex, Text, toast, Breadcrumbs } from "@jsq/ds/core";
import { FormTextInput, FormSelect, SubmitButton } from "@jsq/ds/forms";
import { useOnboardBankAccountToProviderMutation } from "~/graphql/supergraphHooks";
import { type OnboardBankAccountToProviderInput } from "~/graphql/supergraphTypes";
import { SPACING } from "~/styles/variables";
import * as styles from "./styles_vanilla.css";

type FormValues = {
  bankAccountId: string;
  bankSource: string;
};

const validationSchema = Yup.object().shape({
  bankAccountId: Yup.string()
    .required("Bank Account ID is required")
    .matches(/^\d+$/, "Bank Account ID must be a valid integer"),
  bankSource: Yup.string().required("Bank Source is required"),
});

const initialValues: FormValues = {
  bankAccountId: "",
  bankSource: "jp_morgan_chase_bank",
};

const bankSourceOptions = [
  { value: "jp_morgan_chase_bank", label: "JP Morgan Chase" },
  { value: "wells_fargo_bank", label: "Wells Fargo" },
  { value: "silicon_valley_bank", label: "Silicon Valley Bank" },
];

export const OnboardBankAccount: React.FC = () => {
  const [onboardBankAccountToProvider, { loading }] = useOnboardBankAccountToProviderMutation({
    onCompleted: (data) => {
      if (data?.onboardBankAccountToProvider?.id) {
        toast.success("Bank account onboarded successfully");
      }
    },
  });

  const handleSubmit = async (values: FormValues) => {
    try {
      const input: OnboardBankAccountToProviderInput = {
        bankAccountId: parseInt(values.bankAccountId, 10),
        providerName: "fides",
        bankSource: values.bankSource,
      };

      await onboardBankAccountToProvider({
        variables: { onboardBankAccountToProviderInput: input },
      });
    } catch {
      toast.error(
        "The bank account could not be onboarded. Please check the bank account ID and try again.",
      );
    }
  };

  return (
    <Flex
      data-testid="onboard-bank-account-root"
      flexDirection="column"
      className={styles.container}
    >
      <div className={styles.header}>
        <div className={styles.breadcrumbsContainer}>
          <Breadcrumbs
            breadcrumbs={[
              {
                path: "/treasury/configuration",
                name: "Configuration",
                icon: "cog",
              },
              {
                name: "FA treasury (Fides)",
              },
            ]}
          />
        </div>
        <Text variant="regular-xxxl" className={styles.pageTitle}>
          Onboard bank account
        </Text>
      </div>
      <div className={styles.contentArea} data-testid="onboard-bank-account-content">
        <div className={styles.formSection}>
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            <Form>
              <Flex
                flexDirection="row"
                gap={SPACING.MEDIUM}
                alignItems="flex-start"
                className={styles.formFields}
              >
                <div className={styles.formField}>
                  <FormTextInput
                    name="bankAccountId"
                    label="Bank account ID"
                    placeholder="Enter bank account ID"
                    isFullWidth
                  />
                </div>
                <div className={styles.formField}>
                  <FormSelect
                    name="bankSource"
                    label="Source"
                    placeholder="Select bank source"
                    full
                    data-testid="bank-source-select"
                  >
                    <FormSelect.Section>
                      {bankSourceOptions.map((option) => (
                        <FormSelect.Option key={option.value} value={option.value}>
                          {option.label}
                        </FormSelect.Option>
                      ))}
                    </FormSelect.Section>
                  </FormSelect>
                </div>
              </Flex>
              <Flex justifyContent="flex-start">
                <SubmitButton disabled={loading} data-testid="submit-button">
                  Onboard bank account
                </SubmitButton>
              </Flex>
            </Form>
          </Formik>
        </div>
      </div>
    </Flex>
  );
};

export default OnboardBankAccount;
